// @flow
import React from 'react';
import { H3 } from '@finect/tabular-core/Headings';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { ArticleDetail, ArticleSide, SideBlockquote, SidePillRetirement, Video, ArticleCTA } from '../../../components';

type Props = {
  staticContext?: Object,
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Jubilacion = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="Cómo puede un asesor ayudarte a preparar tu jubilación."
      redirectTo={redirectTo}
      toggleModal={toggleModal}
    >
      <Video
        id="Ys9DBFvdfFU"
        src="https://www.youtube.com/embed/Ys9DBFvdfFU?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
      />
      <Paragraph>
        Esa casita junto al mar, quedarte dormido en la siesta con el murmullo de las olas, hacer el viaje que tantos
        años llevas soñando… O, simplemente, mantener el nivel de vida que tienes mientras sigues trabajando.
      </Paragraph>
      <Paragraph>
        Los motivos pueden ser muy diversos, pero la necesidad es común: debemos preparar bien nuestra jubilación para
        alcanzar nuestras metas. Y aquí, la figura de un buen asesor puede ser imprescindible. ¿Por qué?
      </Paragraph>
      <ArticleSide position="right">
        <SidePillRetirement />
      </ArticleSide>
      <H3>Evitar la ‘pensión pública dependencia’.</H3>
      <Paragraph>
        ¿Qué pensión pública te quedará cuando te jubiles? Mucha gente cree que cada vez será menor y acabará siendo
        ridícula; otros, que no será para tanto. Pero lo importante no es tener razón en el debate, sino tener
        suficientes ingresos para poder dejar de trabajar cuando nos hagamos mayores. Elaborar un buen plan y ponerlo en
        marcha cuanto antes resulta básico para evitar depender de la pensión pública al retirarnos y las decisiones
        políticas de corto plazo.
      </Paragraph>
      <H3>Hacer un buen plan de ahorro para la jubilación.</H3>
      <ArticleSide position="left">
        <SideBlockquote right>
          Lo importante es empezar cuanto antes con cantidades pequeñas para ir acumulando sin esfuerzo el capital
          futuro. Y a más años, mayores posibilidades de rentabilizar los ahorros.
        </SideBlockquote>
      </ArticleSide>
      <Paragraph>
        El tiempo puede ser el mejor aliado para nuestros ahorros. Cuanto antes nos iniciemos, más dejaremos trabajar a
        nuestro dinero por nosotros. Warren Buffett, el mejor inversor de todos los tiempos, tiene una frase genial: “Si
        no encuentras una forma de ganar dinero mientras duermes, seguirás trabajando hasta que te mueras”.
      </Paragraph>
      <Paragraph>
        No pierdas tiempo y haz tu plan lo antes posible. Es difícil, pero un asesor trabajará por ti y te lo convertirá
        en ‘fácil’.
      </Paragraph>
      <Video
        inline
        id="o5ThxWtV8bA"
        src="https://www.youtube.com/embed/o5ThxWtV8bA?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
        caption="Luca Lazzarini, Banco Mediolanum."
      />
      <H3>Elegir las inversiones que mejor se adaptan.</H3>
      <Paragraph>
        Si tenemos 30 años y ponemos en marcha desde ya mismo nuestro plan para la jubilación, podremos invertir un alto
        porcentaje de nuestros ahorros en renta variable y aprovechar así su mayor rentabilidad a largo plazo. En
        cambio, si estamos cerca de la edad de retiro, debemos decantarnos por opciones más conservadoras. ¿Te entran
        sudores sólo de pensar en estos líos? Ponte en manos de un asesor.
      </Paragraph>
      <H3>Controlar la evolución de manera periódica.</H3>
      <Paragraph>
        A no ser que te divierta, estar pendiente de cómo van tus inversiones y tu plan para la jubilación puede
        quitarte demasiado tiempo y emociones. Las dudas te asaltarán a lo largo del tiempo. Las emociones te atacarán
        en los baches por los que atraviesan de manera periódica los mercados financieros. Tu asesor será aquí tu
        ‘coach’, que revisará la evolución de tus objetivos y te ayudará a seguir el plan que te habías marcado sin
        dejarte llevar por los miedos y las euforias.
      </Paragraph>
      <ArticleCTA toggleModal={toggleModal} />
      <H3>Aprovechar las ventajas fiscales y evitar pagar de más al retirar el dinero.</H3>
      <Paragraph>
        Uno puede hacerse una cartera de acciones o elegir el producto que te han vendido en la sucursal… E incluso
        elegir bien y tener una buena rentabilidad. Pero quizá haya elegido las peores opciones y pagado muchos
        impuestos a lo largo del tiempo. En nuestro plan para la jubilación, elegir las mejores fórmulas para optimizar
        la factura fiscal puede suponer la diferencia entre comprarte una casa en primera línea de playa para pasar tus
        días de jubilación o tener que quedarte en las afueras.
      </Paragraph>
    </ArticleDetail>
  );
};
